import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Link2, QrCode } from 'lucide-react';
import { CompareIntro } from '@/routes/_layout/compare/-components/compare-intro';
export const Route = createFileRoute('/_layout/compare/')({
    component: ComparePage,
    head: () => ({
        meta: [
            { title: 'Compare Adora — vs Linktree, HeyLink & Flowcode' },
            {
                name: 'description',
                content: 'See how Adora compares to Linktree, HeyLink, and Flowcode. Adora is built for campaign analytics and offline tracking — not just link sharing or QR branding.',
            },
            { property: 'og:title', content: 'Compare Adora — vs Linktree, HeyLink & Flowcode' },
            {
                property: 'og:description',
                content: 'See how Adora compares to Linktree, HeyLink, and Flowcode. Adora is built for campaign analytics and offline tracking — not just link sharing or QR branding.',
            },
        ],
    }),
});
function ComparePage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* Intro */}
            <CompareIntro />

            <Separator className="my-8"/>

            {/* Comparison cards */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <CompareCard title="Adora vs Linktree" description="Link-in-bio pages vs campaign analytics" icon={<Link2 className="h-5 w-5"/>} badges={['Link-in-bio', 'Creators', 'Social']} to="/compare/linktree"/>

                <CompareCard title="Adora vs HeyLink" description="Grouped links vs measurable entry points" icon={<Link2 className="h-5 w-5"/>} badges={['Landing pages', 'Profiles', 'Flyers']} to="/compare/heylink"/>

                <CompareCard title="Adora vs Flowcode" description="QR branding vs offline performance insight" icon={<QrCode className="h-5 w-5"/>} badges={['QR codes', 'Events', 'Branding']} to="/compare/flowcode"/>
            </div>

            <Separator className="my-12"/>

            {/* Positioning reinforcement */}
            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-primary"/>
                            What makes Adora different
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            Adora is not a link-in-bio tool or a QR code generator. It is a campaign analytics platform
                            designed to measure how people discover and interact with your promotions — especially
                            offline.
                        </p>
                        <p>
                            Every QR code or short link becomes part of a measurable campaign, not just a destination.
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Not sure where to start?</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                        <ul className="list-disc pl-4 space-y-1">
                            <li>
                                Sharing multiple links on a profile? →
                                <Link to="/compare/linktree" className="ml-1 text-primary hover:underline">
                                    Compare with Linktree
                                </Link>
                            </li>
                            <li>
                                Using QR codes for events or branding? →
                                <Link to="/compare/flowcode" className="ml-1 text-primary hover:underline">
                                    Compare with Flowcode
                                </Link>
                            </li>
                            <li>
                                Want insight across flyers, menus, and signage? →
                                <Link to="/what-is-adora" className="ml-1 text-primary hover:underline">
                                    Learn what Adora is
                                </Link>
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </main>);
}
const CompareCard = ({ title, description, icon, badges, to }) => (<Card className="h-full transition hover:shadow-md hover:bg-muted/30">
        <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-primary">{icon}</div>
            <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col justify-between gap-4">
            <p className="text-sm text-muted-foreground">{description}</p>

            <div className="flex flex-wrap gap-1">
                {badges.map((b) => (<Badge key={b} variant="secondary">
                        {b}
                    </Badge>))}
            </div>

            <Link to={to} className="text-sm font-medium text-primary hover:underline w-fit">
                View comparison →
            </Link>
        </CardContent>
    </Card>);
CompareCard.displayName = 'CompareCard';
